import API_BASE_URL from '../config/api';
import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

interface AuthContextType {
  username: string | null;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(localStorage.getItem('username'));

  const logout = async () => {
    // Log logout to backend
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Error logging out:', error);
      // Continue with logout even if API call fails
    }
    
    // Clear all user data
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('token');
    setUsername(null);
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setUsername(null);
        return;
      }

      try {
        // const response = await fetch(`${apiUrl}/auth/login`, {
        const response = await fetch(`${API_BASE_URL}/user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.username) {
            setUsername(data.username);
            localStorage.setItem('username', data.username); // Cache username
          }
        } else {
          // Token is invalid, clear everything
          logout();
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        // On error, clear everything
        logout();
      }
    };

    fetchUser();
  }, []);

  // Listen for localStorage changes (for logout from other tabs/components)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'username' || e.key === 'token') {
        const newUsername = localStorage.getItem('username');
        setUsername(newUsername);
      }
    };

    const handleAuthLogin = () => {
      // Force refresh when login event is dispatched
      const newUsername = localStorage.getItem('username');
      setUsername(newUsername);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-login', handleAuthLogin as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-login', handleAuthLogin as EventListener);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ username, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
